import type { User as UserAuth } from "next-auth";
import { useSession } from "next-auth/react";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import React, { useState, useEffect } from "react";

import { useSlideoutContext } from "./Header";

import { IS_VISUAL_REGRESSION_TESTING, ENABLE_PROFILE_SWITCHER } from "@calcom/lib/constants";
import { getPlaceholderAvatar } from "@calcom/lib/defaultAvatarImage";
import { getBookerBaseUrlSync } from "@calcom/lib/getBookerUrl/client";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { UserPermissionRole } from "@calcom/prisma/enums";
import classNames from "@calcom/ui/classNames";
import { Avatar } from "@calcom/ui/components/avatar";
import { Credits } from "@calcom/ui/components/credits";
import { ButtonOrLink } from "@calcom/ui/components/dropdown";
import { Icon } from "@calcom/ui/components/icon";
import { Logo } from "@calcom/ui/components/logo";
import { SkeletonText } from "@calcom/ui/components/skeleton";
import { Tooltip } from "@calcom/ui/components/tooltip";

import { KBarTrigger } from "../kbar/Kbar";
import { Navigation } from "./navigation/Navigation";
import { useBottomNavItems } from "./useBottomNavItems";
import { ProfileDropdown } from "./user-dropdown/ProfileDropdown";
import { UserDropdown } from "./user-dropdown/UserDropdown";

// need to import without ssr to prevent hydration errors
const Tips = dynamic(() => import("@calcom/features/tips").then((mod) => mod.Tips), {
  ssr: false,
});

export type SideBarContainerProps = {
  bannersHeight: number;
  isPlatformUser?: boolean;
};

export type SideBarProps = {
  bannersHeight: number;
  user?: UserAuth | null;
  isPlatformUser?: boolean;
};

export function SideBarContainer({ bannersHeight, isPlatformUser = false }: SideBarContainerProps) {
  const { status, data } = useSession();

  // Make sure that Sidebar is rendered optimistically so that a refresh of pages when logged in have SideBar from the beginning.
  // This improves the experience of refresh on app store pages(when logged in) which are SSG.
  // Though when logged out, app store pages would temporarily show SideBar until session status is confirmed.
  if (status !== "loading" && status !== "authenticated") return null;
  return <SideBar isPlatformUser={isPlatformUser} bannersHeight={bannersHeight} user={data?.user} />;
}

export function SideBar({ bannersHeight, user }: SideBarProps) {
  const session = useSession();
  const { t, isLocaleReady } = useLocale();
  const pathname = usePathname();
  const isPlatformPages = pathname?.startsWith("/settings/platform");
  const isAdmin = session.data?.user.role === UserPermissionRole.ADMIN;
  const { isSidebarCollapsed, setIsSidebarCollapsed, isSlideoutOpen } = useSlideoutContext();
  const [localIsCollapsed, setLocalIsCollapsed] = useState(false);
  const [version, setVersion] = useState<string>("");
  const [isSourceConnect, setIsSourceConnect] = useState(false);

  // Use context state if available, otherwise fall back to local state
  const isCollapsed = isSidebarCollapsed !== undefined ? isSidebarCollapsed : localIsCollapsed;
  const setIsCollapsed = setIsSidebarCollapsed !== undefined ? setIsSidebarCollapsed : setLocalIsCollapsed;

  // Restore sidebar when slideout is closed
  React.useEffect(() => {
    if (!isSlideoutOpen && isSidebarCollapsed) {
      setIsSidebarCollapsed(false);
    }
  }, [isSlideoutOpen, isSidebarCollapsed, setIsSidebarCollapsed]);

  useEffect(() => {
    fetch("/api/version")
      .then(res => res.json())
      .then(data => setVersion(data.version))
      .catch(() => setVersion(""));
  }, []);

  // Check if source=connect is in localStorage
  useEffect(() => {
    if (typeof window !== "undefined") {
      const signupSource = localStorage.getItem("signupSource");
      setIsSourceConnect(signupSource === "connect");
    }
  }, []);

  const publicPageUrl = `${getBookerBaseUrlSync(user?.org?.slug ?? null)}/${user?.orgAwareUsername}`;

  const bottomNavItems = useBottomNavItems({
    publicPageUrl,
    isAdmin,
    user,
  });

  const sidebarStylingAttributes = {
    maxHeight: `calc(100vh - ${bannersHeight}px)`,
    top: `${bannersHeight}px`,
  };

  return (
    <div className="relative">
      <style jsx>{`
        main::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <aside
        style={!isPlatformPages ? sidebarStylingAttributes : {}}
        className={classNames(
          "fixed left-0 hidden h-full flex-col overflow-y-auto overflow-x-hidden pt-3 md:sticky md:flex transition-all duration-300",
          isCollapsed ? "w-14 lg:px-1" : "w-14 lg:w-56 lg:px-3",
          !isPlatformPages && "max-h-screen"
        )}>
        <div className="flex h-full flex-col justify-between">
          <header className="todesktop:flex-col-reverse todesktop:[-webkit-app-region:drag] items-center justify-between border-b md:hidden lg:flex">
            {
              // isSourceConnect ? (
                !isCollapsed ? (
                  <div className="flex justify-center pb-4 w-full">
                    <Image
                      src="/images/raiqa-connect-logo.png"
                      alt="Raiqa Connect"
                      width={100}
                      height={28}
                      className="h-7 w-auto"
                      style={{ objectFit: "contain" }}
                    />
                  </div>
                ) : null
              // ) 
              //  (
              //   isCollapsed ? (
              //     <svg width="40" height="50" viewBox="0 0 356 356" fill="none" xmlns="http://www.w3.org/2000/svg">
              //       <g clip-path="url(#clip0_366_6522)">
              //         <path d="M170.88 220.72H135.28V313.28C135.28 336.874 154.406 356 178 356C201.594 356 220.72 336.874 220.72 313.28V220.72H185.12V313.28C185.12 317.212 181.932 320.4 178 320.4C174.068 320.4 170.88 317.212 170.88 313.28V220.72Z" fill="url(#paint0_radial_366_6522)" />
              //         <path d="M220.719 185.12V220.72H313.279C336.873 220.72 355.999 201.593 355.999 178C355.999 154.406 336.873 135.28 313.279 135.28H220.719L220.719 170.88H313.279C317.212 170.88 320.399 174.068 320.399 178C320.399 181.932 317.212 185.12 313.279 185.12H220.719Z" fill="url(#paint1_radial_366_6522)" />
              //         <path d="M185.12 135.28H220.72V42.72C220.72 19.1264 201.594 0 178 0C154.406 0 135.28 19.1264 135.28 42.72V135.28H170.88V42.72C170.88 38.7877 174.068 35.6 178 35.6C181.932 35.6 185.12 38.7877 185.12 42.72V135.28Z" fill="url(#paint2_radial_366_6522)" />
              //         <path d="M135.28 170.88V135.28H42.72C19.1264 135.28 0 154.406 0 178C0 201.593 19.1264 220.72 42.72 220.72H135.28V185.12H42.72C38.7877 185.12 35.6 181.932 35.6 178C35.6 174.068 38.7877 170.88 42.72 170.88H135.28Z" fill="url(#paint3_radial_366_6522)" />
              //         <path d="M135.28 263.44H263.44L220.72 220.72H135.28V135.28L92.5601 92.5601V307.94L135.28 263.44Z" fill="url(#paint4_linear_366_6522)" />
              //         <path d="M263.422 263.44L263.44 50.73L220.702 92.56H92.5601L135.262 135.262V135.28H220.72V220.72H220.702L263.422 263.44Z" fill="url(#paint5_linear_366_6522)" />
              //       </g>
              //       <defs>
              //         <radialGradient id="paint0_radial_366_6522" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(220.72 242.08) rotate(126.87) scale(142.4 100.485)">
              //           <stop stop-color="#C6F7FA" />
              //           <stop offset="0.181205" stop-color="#1BE1EA" />
              //           <stop offset="1" stop-color="#5536F0" />
              //         </radialGradient>
              //         <radialGradient id="paint1_radial_366_6522" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(242.079 135.28) rotate(36.8699) scale(142.4 100.485)">
              //           <stop stop-color="#C6F7FA" />
              //           <stop offset="0.181205" stop-color="#1BE1EA" />
              //           <stop offset="1" stop-color="#5536F0" />
              //         </radialGradient>
              //         <radialGradient id="paint2_radial_366_6522" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(135.28 113.92) rotate(-53.1301) scale(142.4 100.485)">
              //           <stop stop-color="#C6F7FA" />
              //           <stop offset="0.181205" stop-color="#1BE1EA" />
              //           <stop offset="1" stop-color="#5536F0" />
              //         </radialGradient>
              //         <radialGradient id="paint3_radial_366_6522" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(113.92 220.72) rotate(-143.13) scale(142.4 100.485)">
              //           <stop stop-color="#C6F7FA" />
              //           <stop offset="0.181205" stop-color="#1BE1EA" />
              //           <stop offset="1" stop-color="#5536F0" />
              //         </radialGradient>
              //         <linearGradient id="paint4_linear_366_6522" x1="87.2201" y1="123.71" x2="240.3" y2="275.9" gradientUnits="userSpaceOnUse">
              //           <stop stop-color="#FFA537" />
              //           <stop offset="0.25" stop-color="#FF7139" />
              //           <stop offset="0.5" stop-color="#FD2C3F" />
              //           <stop offset="0.755" stop-color="#FF2185" />
              //           <stop offset="1" stop-color="#B830E1" />
              //         </linearGradient>
              //         <linearGradient id="paint5_linear_366_6522" x1="92.5601" y1="50.73" x2="179.539" y2="292.203" gradientUnits="userSpaceOnUse">
              //           <stop stop-color="#806EEE" />
              //           <stop offset="1" stop-color="#3116C8" />
              //         </linearGradient>
              //         <clipPath id="clip0_366_6522">
              //           <rect width="356" height="356" fill="white" />
              //         </clipPath>
              //       </defs>
              //     </svg>
              //   ) : (
              //     <svg width="1000" height="56" viewBox="0 0 1485 556" fill="none" xmlns="http://www.w3.org/2000/svg">
              //       <g clip-path="url(#clip0_366_6545)">
              //         <path d="M270.88 320.72H235.28V413.28C235.28 436.874 254.406 456 278 456C301.594 456 320.72 436.874 320.72 413.28V320.72H285.12V413.28C285.12 417.212 281.932 420.4 278 420.4C274.068 420.4 270.88 417.212 270.88 413.28V320.72Z" fill="url(#paint0_radial_366_6545)" />
              //         <path d="M320.719 285.12V320.72H413.279C436.873 320.72 455.999 301.593 455.999 278C455.999 254.406 436.873 235.28 413.279 235.28H320.719V270.88H413.279C417.212 270.88 420.399 274.068 420.399 278C420.399 281.932 417.212 285.12 413.279 285.12H320.719Z" fill="url(#paint1_radial_366_6545)" />
              //         <path d="M285.12 235.28H320.72V142.72C320.72 119.126 301.594 100 278 100C254.406 100 235.28 119.126 235.28 142.72V235.28H270.88V142.72C270.88 138.788 274.068 135.6 278 135.6C281.932 135.6 285.12 138.788 285.12 142.72V235.28Z" fill="url(#paint2_radial_366_6545)" />
              //         <path d="M235.28 270.88V235.28H142.72C119.126 235.28 100 254.406 100 278C100 301.593 119.126 320.72 142.72 320.72H235.28V285.12H142.72C138.788 285.12 135.6 281.932 135.6 278C135.6 274.068 138.788 270.88 142.72 270.88H235.28Z" fill="url(#paint3_radial_366_6545)" />
              //         <path d="M235.28 363.44H363.44L320.72 320.72H235.28V235.28L192.56 192.56V407.94L235.28 363.44Z" fill="url(#paint4_linear_366_6545)" />
              //         <path d="M363.422 363.44L363.44 150.73L320.702 192.56H192.56L235.262 235.262V235.28H320.72V320.72H320.702L363.422 363.44Z" fill="url(#paint5_linear_366_6545)" />
              //       </g>
              //       <path d="M730.951 207.159C730.951 213.414 725.924 218.318 719.724 218.318C713.525 218.318 708.498 213.414 708.498 207.159C708.498 200.903 713.525 196 719.724 196C725.924 196 730.951 200.903 730.951 207.159Z" fill="black" />
              //       <path d="M710.677 322.027V247.973C710.677 242.901 714.698 238.843 719.724 238.843C724.751 238.843 728.772 242.901 728.772 247.973V322.027C728.772 327.099 724.751 331.157 719.724 331.157C714.698 331.157 710.677 327.099 710.677 322.027Z" fill="black" />
              //       <path d="M642.939 330.804H684.492C690.357 330.804 693.54 326.746 693.54 321.674V274.841C693.54 256.243 678.795 241.196 660.029 241.196H631.545C626.686 241.196 622.665 245.254 622.665 250.157C622.665 255.06 626.686 259.118 631.545 259.118H660.029C669.245 259.118 675.444 266.049 675.444 275.01V312.882H642.939C637.41 312.882 633.556 308.148 633.556 303.076C633.556 298.004 637.41 293.27 642.939 293.27H659.862C664.721 293.27 668.742 289.212 668.742 284.309C668.742 279.406 664.721 275.348 659.862 275.348H642.939C626.686 275.348 615.46 287.86 615.46 303.076C615.46 318.293 626.686 330.804 642.939 330.804Z" fill="black" />
              //       <path d="M868.991 331.608H910.544C916.408 331.608 919.592 327.551 919.592 322.478V275.645C919.592 257.047 904.847 242 886.081 242H857.597C852.738 242 848.716 246.058 848.716 250.961C848.716 255.864 852.738 259.922 857.597 259.922H886.081C895.297 259.922 901.496 266.854 901.496 275.814V313.687H868.991C863.461 313.687 859.608 308.953 859.608 303.88C859.608 298.808 863.461 294.074 868.991 294.074H885.914C890.773 294.074 894.794 290.016 894.794 285.113C894.794 280.21 890.773 276.153 885.914 276.153H868.991C852.738 276.153 841.512 288.664 841.512 303.88C841.512 319.097 852.738 331.608 868.991 331.608Z" fill="black" />
              //       <path fill-rule="evenodd" clip-rule="evenodd" d="M809.351 350.863V331.608H788.742C761.934 331.608 743 311.489 743 286.804C743 262.12 761.934 242 788.742 242H818.399C823.426 242 827.447 246.058 827.447 251.13V350.863C827.447 355.935 823.426 359.993 818.399 359.993C813.373 359.993 809.351 355.935 809.351 350.863ZM809.351 259.922H788.742C772.657 259.922 761.096 272.095 761.096 286.804C761.096 301.513 772.657 313.687 788.742 313.687H809.351V259.922Z" fill="black" />
              //       <path d="M550.507 283.87L591.238 328.981C594.59 332.671 600.457 333.006 604.145 329.652C608 326.298 608.168 320.429 604.815 316.739L576.99 286.05C595.093 285.882 608 272.298 608 255.193C608 237.919 594.59 224 576.32 224H529.219C524.19 224 520 228.193 520 233.224V322.776C520 327.807 524.19 332 529.219 332C534.248 332 538.438 327.807 538.438 322.776V241.776H576.152C584.366 241.776 589.562 247.814 589.562 255.193C589.562 262.571 584.366 268.441 576.152 268.441H557.211C552.183 268.441 547.992 272.634 547.992 277.665C547.992 280.012 548.998 282.193 550.507 283.87Z" fill="black" />
              //       <path d="M1385 326.274H1375.71V285.769C1375.71 269.434 1365.89 260.138 1353.15 260.138C1339.08 260.138 1330.46 270.895 1330.46 285.105V326.274H1321.17V228H1330.46V262.927H1330.72C1335.37 255.888 1343.6 251.772 1353.02 251.772C1373.06 251.772 1385 265.583 1385 285.769V326.274Z" fill="#000002" />
              //       <path d="M1294.38 326.274H1285.09V261.997H1269.83V253.631H1285.09V228H1294.38V253.631H1311.76V261.997H1294.38V326.274Z" fill="#000002" />
              //       <path d="M1259.32 326.274H1250.03V228H1259.32V326.274Z" fill="#000002" />
              //       <path d="M1233.23 326.273H1223.94V313.524H1223.67C1217.7 322.555 1207.21 328 1195.4 328C1173.64 328 1156.39 311.532 1156.39 289.753C1156.39 267.973 1174.17 251.771 1195.4 251.771C1207.48 251.771 1217.83 257.216 1223.67 266.38H1223.94V253.631H1233.23V326.273ZM1195.4 319.633C1211.59 319.633 1224.47 306.353 1224.47 290.018C1224.47 273.817 1211.73 260.138 1195.27 260.138C1178.55 260.138 1165.68 272.887 1165.68 290.151C1165.68 306.884 1178.28 319.633 1195.4 319.633Z" fill="#000002" />
              //       <path d="M1145.19 293.339H1079.1C1079.9 307.814 1093.17 319.633 1107.23 319.633C1118.51 319.633 1129.26 313.126 1133.24 302.502H1143.07C1137.49 318.173 1122.89 328 1107.37 328C1086.8 328 1069.55 311.267 1069.55 290.018C1069.55 268.903 1086.93 251.771 1107.23 251.771C1129 251.771 1145.32 270.497 1145.32 290.55C1145.32 291.479 1145.19 292.409 1145.19 293.339ZM1079.1 284.972H1135.77C1133.11 270.364 1121.57 260.138 1107.23 260.138C1093.7 260.138 1081.62 269.965 1079.1 284.972Z" fill="#000002" />
              //       <path d="M1054.47 326.274H1044.65V280.191H993.82V326.274H984V228H993.82V271.293H1044.65V228H1054.47V326.274Z" fill="#000002" />
              //       <defs>
              //         <radialGradient id="paint0_radial_366_6545" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(320.72 342.08) rotate(126.87) scale(142.4 100.485)">
              //           <stop stop-color="#C6F7FA" />
              //           <stop offset="0.181205" stop-color="#1BE1EA" />
              //           <stop offset="1" stop-color="#5536F0" />
              //         </radialGradient>
              //         <radialGradient id="paint1_radial_366_6545" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(342.079 235.28) rotate(36.8699) scale(142.4 100.485)">
              //           <stop stop-color="#C6F7FA" />
              //           <stop offset="0.181205" stop-color="#1BE1EA" />
              //           <stop offset="1" stop-color="#5536F0" />
              //         </radialGradient>
              //         <radialGradient id="paint2_radial_366_6545" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(235.28 213.92) rotate(-53.1301) scale(142.4 100.485)">
              //           <stop stop-color="#C6F7FA" />
              //           <stop offset="0.181205" stop-color="#1BE1EA" />
              //           <stop offset="1" stop-color="#5536F0" />
              //         </radialGradient>
              //         <radialGradient id="paint3_radial_366_6545" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(213.92 320.72) rotate(-143.13) scale(142.4 100.485)">
              //           <stop stop-color="#C6F7FA" />
              //           <stop offset="0.181205" stop-color="#1BE1EA" />
              //           <stop offset="1" stop-color="#5536F0" />
              //         </radialGradient>
              //         <linearGradient id="paint4_linear_366_6545" x1="187.22" y1="223.71" x2="340.3" y2="375.9" gradientUnits="userSpaceOnUse">
              //           <stop stop-color="#FFA537" />
              //           <stop offset="0.25" stop-color="#FF7139" />
              //           <stop offset="0.5" stop-color="#FD2C3F" />
              //           <stop offset="0.755" stop-color="#FF2185" />
              //           <stop offset="1" stop-color="#B830E1" />
              //         </linearGradient>
              //         <linearGradient id="paint5_linear_366_6545" x1="192.56" y1="150.73" x2="279.539" y2="392.203" gradientUnits="userSpaceOnUse">
              //           <stop stop-color="#806EEE" />
              //           <stop offset="1" stop-color="#3116C8" />
              //         </linearGradient>
              //         <clipPath id="clip0_366_6545">
              //           <rect width="356" height="356" fill="white" transform="translate(100 100)" />
              //         </clipPath>
              //       </defs>
              //     </svg>
              //   )
              // )
            }


            <div className="flex justify-end rtl:space-x-reverse">
              <button
                color="minimal"
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="todesktop:block hover:text-emphasis hover:bg-subtle text-subtle group text-sm font-medium p-1 rounded-md">
                {isCollapsed ? (
                  <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 12.0011L10 8.0011L6 4.0011" stroke="#010623" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 12.0011L6 8.0011L10 4.0011" stroke="#010623" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
              <button
                color="minimal"
                onClick={() => window.history.forward()}
                className="todesktop:block hover:text-emphasis text-subtle group hidden text-sm font-medium">
                <Icon
                  name="arrow-right"
                  className="group-hover:text-emphasis text-subtle h-4 w-4 flex-shrink-0"
                />
              </button>
              {/*{!!user?.org && (*/}
              {/*  <div data-testid="user-dropdown-trigger" className="flex items-center">*/}
              {/*    <UserDropdown small />*/}
              {/*  </div>*/}
              {/*)}*/}
              {/*<KBarTrigger />*/}
            </div>
          </header>
          {/* logo icon for tablet */}
          <Link href="/event-types" className="text-center md:inline lg:hidden">
            <Logo small icon />
          </Link>
          <Navigation isPlatformNavigation={isPlatformPages} isCollapsed={isCollapsed} />
        </div>

        {!isPlatformPages && (
          <div>
            {/*<Tips />*/}
            {bottomNavItems.map((item, index) => (
              <Tooltip side="right" content={t(item.name)} className="lg:hidden" key={item.name}>
                <ButtonOrLink
                  id={item.name}
                  href={item.href || undefined}
                  aria-label={t(item.name)}
                  target={item.target}
                  className={classNames(
                    "text-left",
                    "[&[aria-current='page']]:!bg-[var(--primary-main)] text-default justify-right group flex items-center rounded-xl px-2 py-1.5 text-sm font-medium transition",
                    "[&[aria-current='page']]:text-emphasis mt-0.5 w-full text-sm",
                    isLocaleReady ? "hover:!bg-[var(--primary-main)] hover:text-white" : "",
                    index === 0 && "mt-3"
                  )}
                  onClick={item.onClick}>
                  {item.customIconElement ? (
                    <div className="h-8 w-8 flex-shrink-0 me-3 md:mx-auto lg:ltr:mr-2 lg:rtl:ml-2">
                      {item.customIconElement}
                    </div>
                  ) : !!item.icon && (
                    <Icon
                      name={item.isLoading ? "rotate-cw" : item.icon}
                      className={classNames(
                        "h-4 w-4 flex-shrink-0 [&[aria-current='page']]:text-inherit",
                        "me-3 md:mx-auto lg:ltr:mr-2 lg:rtl:ml-2",
                        item.isLoading && "animate-spin"
                      )}
                      aria-hidden="true"
                    />
                  )}
                  {isLocaleReady ? (
                    <span className={classNames("hidden w-full justify-between lg:flex", isCollapsed && "lg:hidden")}>
                      <div className="flex">{t(item.name)}</div>
                    </span>
                  ) : (
                    <SkeletonText className="h-[20px] w-full" />
                  )}
                </ButtonOrLink>
              </Tooltip>
            ))}
            {/*{!IS_VISUAL_REGRESSION_TESTING && <Credits />}*/}

            {/* Version Display */}
            {version && version !== "0.0.0" && (
              <div className={classNames(
                "mt-2 px-2 py-1 text-xs text-gray-400 font-mono text-center",
                isCollapsed && "lg:hidden"
              )}>
                v{version}
              </div>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}
